function toDate (value) {
  if (value && typeof value.toDate === "function") return value.toDate();
  return new Date(value);
}

function dateKey (date) {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

function sundayBefore (date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - d.getDay());
  return d;
}

function squadDisplayName (squad) {
  return squad.title || squad.name || squad.id;
}

function locationDisplayName (location) {
  return location.name || location.id;
}

function usableSlots (slots) {
  return (slots || []).filter(slot =>
    Number.isInteger(slot.dayOfWeek) &&
    slot.dayOfWeek >= 0 &&
    slot.dayOfWeek <= 6 &&
    Number.isFinite(slot.time)
  );
}

// Circle method: n - 1 rounds (n rounds with a bye for odd n), every squad plays at most once per round.
function buildRoundRobinRounds (squads) {
  const arr = [...squads];
  if (arr.length % 2 === 1) arr.push(null);
  const count = arr.length;
  const rounds = [];
  for (let round = 0; round < count - 1; round++) {
    const pairs = [];
    for (let i = 0; i < count / 2; i++) {
      const left = arr[i];
      const right = arr[count - 1 - i];
      if (left !== null && right !== null) pairs.push([left, right]);
    }
    rounds.push(pairs);
    arr.splice(1, 0, arr.pop());
  }
  return rounds;
}

// The dates for the given weekday in calendar weeks 1..count, anchored at
// sundayAlignedStart. Blackout avoidance is handled by `openSlot` during
// placement, so every returned date is used by exactly one placed game.
function weekdayOccurrences ({ sundayAlignedStart, dayOfWeek, count }) {
  const dates = [];
  for (let i = 0; i < count; i++) {
    const date = new Date(sundayAlignedStart);
    date.setDate(date.getDate() + i * 7 + dayOfWeek);
    dates.push(date);
  }
  return dates;
}

// The fewest weeks a schedule could take, given venue table capacity and how many times a
// squad can race in a single week. Mirrors the length check in utils/schedule-checks.js:
// the stronger of venue capacity and squad pacing (or the round-robin shape when a squad can
// only race once a week).
function computeFloorWeeks (squads, locations, passes, designatedPerVenue) {
  const slotsByWeekday = new Map();
  for (const location of locations) {
    for (const slot of location.slots || []) {
      const key = `${location.id}|${slot.dayOfWeek}`;
      if (!slotsByWeekday.has(key)) slotsByWeekday.set(key, new Set());
      slotsByWeekday.get(key).add(slot.time);
    }
  }
  let maxGamesPerWeek = 1;
  for (const times of slotsByWeekday.values()) {
    maxGamesPerWeek = Math.max(maxGamesPerWeek, times.size);
  }
  const n = squads.length;
  let capacityWeeks = 0;
  for (const location of locations) {
    const weeklyGames = location.numberOfTables * (location.slots || []).length;
    if (weeklyGames > 0) {
      capacityWeeks = Math.max(capacityWeeks, Math.ceil((designatedPerVenue.get(location.id) || 0) / weeklyGames));
    }
  }
  if (maxGamesPerWeek === 1) {
    return Math.max(capacityWeeks, (n % 2 === 1 ? n : n - 1) * passes);
  }
  return Math.max(capacityWeeks, Math.ceil((n - 1) * passes / maxGamesPerWeek));
}

// Orients every edge of a multigraph so each node's in-degree and out-degree differ by at
// most one. Odd-degree nodes are joined to a dummy node so every node has even degree, then
// each Euler circuit is walked (Hierholzer) and edges are oriented in walking direction.
// Every closed walk contributes equally to in- and out-degree, so the result is balanced.
// Returns, for each edge, the node it points into (the "host"). Linear in the edge count.
function balancedOrientation (nodes, edges) {
  const dummy = Symbol("dummy");
  const adjacency = new Map([...nodes, dummy].map(node => [node, []]));
  const degree = new Map(nodes.map(node => [node, 0]));
  const links = edges.map((edge, index) => ({ u: edge.u, v: edge.v, index, used: false }));
  for (const link of links) {
    degree.set(link.u, degree.get(link.u) + 1);
    degree.set(link.v, degree.get(link.v) + 1);
  }
  for (const node of nodes) {
    if (degree.get(node) % 2 === 1) links.push({ u: node, v: dummy, index: -1, used: false });
  }
  for (const link of links) {
    adjacency.get(link.u).push(link);
    adjacency.get(link.v).push(link);
  }
  const nextLink = new Map([...adjacency.keys()].map(node => [node, 0]));
  const host = new Array(edges.length);
  for (const start of adjacency.keys()) {
    const stack = [start];
    while (stack.length > 0) {
      const node = stack[stack.length - 1];
      const list = adjacency.get(node);
      let p = nextLink.get(node);
      while (p < list.length && list[p].used) p++;
      nextLink.set(node, p);
      if (p === list.length) {
        stack.pop();
        continue;
      }
      const link = list[p];
      link.used = true;
      const target = link.u === node ? link.v : link.u;
      if (link.index >= 0) host[link.index] = target;
      stack.push(target);
    }
  }
  return host;
}

import { buildBracketTree, hasConsolation, nextPowerOf2 } from "@/utils/league-bracket";

export function generateLeagueSchedule (league) {
  const squads = league.squads;
  const locations = league.locations || [];
  const passes = league.timesEachPairPlays;
  const errors = [];

  const locationById = new Map(locations.map(loc => [loc.id, loc]));
  const locationOf = new Map();
  const slotsOf = new Map();
  const reportedLocations = new Set();

  for (const squad of squads) {
    const loc = squad.locationId ? locationById.get(squad.locationId) : null;
    if (!loc) {
      errors.push(`"${squadDisplayName(squad)}" needs a home location to be scheduled.`);
      continue;
    }
    const slots = usableSlots(loc.slots);
    if (slots.length === 0 || loc.numberOfTables < 1) {
      if (!reportedLocations.has(loc.id)) {
        reportedLocations.add(loc.id);
        errors.push(slots.length === 0
          ? `"${locationDisplayName(loc)}" needs at least one time slot before it can host games.`
          : `"${locationDisplayName(loc)}" needs at least one table before it can host games.`);
      }
      continue;
    }
    locationOf.set(squad.id, loc);
    slotsOf.set(loc.id, slots);
  }

  if (errors.length > 0 || squads.length < 2 || passes < 1) {
    return { errors, events: [] };
  }

  const startDate = toDate(league.startDate);
  const blackoutSet = new Set((league.blackoutDates || []).map(d => (typeof d === "string" ? d : dateKey(toDate(d)))));
  const sundayAlignedStart = sundayBefore(startDate);
  const internalBlackoutSet = new Set(blackoutSet);
  {
    const cursor = new Date(sundayAlignedStart);
    while (cursor.getTime() < startDate.getTime()) {
      internalBlackoutSet.add(dateKey(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
  }
  const rounds = buildRoundRobinRounds(squads);
  const venueOf = squad => locationOf.get(squad.id);
  const isCross = ([a, b]) => venueOf(a) !== venueOf(b);

  // ---------------------------------------------------------------------------
  // Home/away designation. Travel fairness is a balanced-orientation problem on the
  // cross-venue games, solved exactly by Euler circuits:
  //  - Passes come in mirrored pairs: pass 2k gets a per-round orientation balanced at the
  //    venue level (so no venue hosts more than half of its round's cross games) and pass
  //    2k+1 reverses it. Every squad then hosts each cross opponent exactly once per pair.
  //  - An odd final pass is balanced at the squad level, so each squad is within half a
  //    game of hosting half of that pass's cross games.
  //  - Same-venue games never involve travel; their home label is balanced for appearance.
  // ---------------------------------------------------------------------------
  // roundGames[pass][roundIndex][pairIndex] mirrors rounds[roundIndex][pairIndex] for each pass.
  const roundGames = [];
  for (let pass = 0; pass < passes; pass++) {
    roundGames.push(rounds.map(round => round.map(([a, b]) => ({ a, b, home: null }))));
  }
  const games = roundGames.flat(2);

  const pairedPasses = passes - (passes % 2);
  rounds.forEach((round, roundIndex) => {
    const crossIndexes = round.map((pair, pairIndex) => pairIndex).filter(pairIndex => isCross(round[pairIndex]));
    const venues = [...new Set(crossIndexes.flatMap(pairIndex => round[pairIndex].map(squad => venueOf(squad).id)))];
    const hosts = balancedOrientation(venues, crossIndexes.map(pairIndex => ({
      u: venueOf(round[pairIndex][0]).id,
      v: venueOf(round[pairIndex][1]).id
    })));
    crossIndexes.forEach((pairIndex, i) => {
      const [a, b] = round[pairIndex];
      const first = venueOf(a).id === hosts[i] ? a : b;
      const second = first === a ? b : a;
      for (let pass = 0; pass < pairedPasses; pass++) {
        roundGames[pass][roundIndex][pairIndex].home = pass % 2 === 0 ? first : second;
      }
    });
  });

  function designateBySquad (list) {
    const hosts = balancedOrientation(squads.map(s => s.id), list.map(game => ({ u: game.a.id, v: game.b.id })));
    list.forEach((game, i) => {
      game.home = hosts[i] === game.a.id ? game.a : game.b;
    });
  }
  if (passes % 2 === 1) {
    designateBySquad(roundGames[passes - 1].flat().filter(game => isCross([game.a, game.b])));
  }
  designateBySquad(games.filter(game => !isCross([game.a, game.b])));

  const designatedPerVenue = new Map(locations.map(loc => [loc.id, 0]));
  for (const game of games) {
    const locId = venueOf(game.home).id;
    designatedPerVenue.set(locId, (designatedPerVenue.get(locId) || 0) + 1);
  }

  // ---------------------------------------------------------------------------
  // Placement. Weeks 1..floorWeeks are the season budget; games are placed there first with
  // their designated home venue, and only fall to the opponent's venue (or past the budget)
  // when they cannot. Flips choose the side whose squad is furthest below its fair hosting
  // share, so the residual travel skew stays as small as a season at the floor allows.
  // ---------------------------------------------------------------------------
  const floorWeeks = computeFloorWeeks(squads, locations, passes, designatedPerVenue);

  // Fair-hosting targets: each squad should host about half of its cross-venue games. Flips
  // that must happen to fit the season budget are steered toward the squad furthest below
  // this share, keeping the residual travel skew as small as possible.
  const crossGames = new Map(squads.map(s => [s.id, 0]));
  for (let i = 0; i < squads.length; i++) {
    for (let j = i + 1; j < squads.length; j++) {
      if (locationOf.get(squads[i].id).id !== locationOf.get(squads[j].id).id) {
        crossGames.set(squads[i].id, crossGames.get(squads[i].id) + passes);
        crossGames.set(squads[j].id, crossGames.get(squads[j].id) + passes);
      }
    }
  }
  const crossTarget = new Map(squads.map(s => [s.id, crossGames.get(s.id) / 2]));
  const hostedCross = new Map(squads.map(s => [s.id, 0]));

  const weeks = [];
  function weekState (week) {
    while (weeks.length < week) weeks.push({ slotLoad: new Map(), bookings: new Map() });
    return weeks[week - 1];
  }

  function squadFree (state, squadId, slot, locationId) {
    const booking = state.bookings.get(squadId);
    return !booking || (booking.dayOfWeek === slot.dayOfWeek && booking.locationId === locationId && !booking.times.has(slot.time));
  }

  function openSlot (state, game, loc, week) {
    const slots = slotsOf.get(loc.id);
    for (let slotIndex = 0; slotIndex < slots.length; slotIndex++) {
      const slot = slots[slotIndex];
      if ((state.slotLoad.get(`${loc.id}|${slotIndex}`) || 0) >= loc.numberOfTables) continue;
      if (squadFree(state, game.a.id, slot, loc.id) && squadFree(state, game.b.id, slot, loc.id)) {
        const slotDate = new Date(sundayAlignedStart);
        slotDate.setDate(slotDate.getDate() + (week - 1) * 7 + slot.dayOfWeek);
        if (internalBlackoutSet.has(dateKey(slotDate))) continue;
        return slotIndex;
      }
    }
    return -1;
  }

  function freeTables (state, loc) {
    let free = 0;
    for (let slotIndex = 0; slotIndex < slotsOf.get(loc.id).length; slotIndex++) {
      free += loc.numberOfTables - (state.slotLoad.get(`${loc.id}|${slotIndex}`) || 0);
    }
    return free;
  }

  // Places as many of a round's games as fit in the week at their designated venue and (inside
  // the week budget) resolves venue overloads by pushing games to the other squad's venue along
  // shortest alternating chains, preferring pushes that move hosting toward the squad that is
  // furthest below its fair share. Beyond the budget a game never leaves its designated venue.
  function fitRound (batch, week) {
    const budgeted = week <= floorWeeks;
    const state = weekState(week);
    const capacity = new Map();
    const load = new Map();
    const hostedBy = new Map();
    for (const game of batch) {
      for (const squad of [game.a, game.b]) {
        const loc = venueOf(squad);
        if (!capacity.has(loc.id)) {
          capacity.set(loc.id, freeTables(state, loc));
          load.set(loc.id, 0);
          hostedBy.set(loc.id, []);
        }
      }
    }
    function otherVenue (game, loc) { return venueOf(game.a) === loc ? venueOf(game.b) : venueOf(game.a); }
    const homeSquadAt = (game, loc) => (venueOf(game.home) === loc ? game.home : (game.home === game.a ? game.b : game.a));
    const venueOfGame = new Map();
    const assign = (game, loc) => {
      venueOfGame.set(game, loc);
      load.set(loc.id, load.get(loc.id) + 1);
      hostedBy.get(loc.id).push(game);
      if (venueOf(game.a) !== venueOf(game.b)) {
        hostedCross.set(homeSquadAt(game, loc).id, hostedCross.get(homeSquadAt(game, loc).id) + 1);
      }
    };
    const unassign = game => {
      const loc = venueOfGame.get(game);
      venueOfGame.delete(game);
      load.set(loc.id, load.get(loc.id) - 1);
      hostedBy.get(loc.id).splice(hostedBy.get(loc.id).indexOf(game), 1);
      if (venueOf(game.a) !== venueOf(game.b)) {
        hostedCross.set(homeSquadAt(game, loc).id, hostedCross.get(homeSquadAt(game, loc).id) - 1);
      }
    };

    // A game only leaves its designated venue when that venue is saturated: it has no free slot
    // in any future week through twice the floor horizon. It never flips merely because this
    // week happens to be full, so the fair home/away designation is preserved unless capacity
    // is genuinely exhausted.
    const designatedSaturated = game => {
      const preferred = venueOf(game.home);
      const horizon = floorWeeks * 2;
      for (let w = week + 1; w <= horizon; w++) {
        if (openSlot(weekState(w), game, preferred, w) >= 0) return false;
      }
      return true;
    };

    const waiting = [];
    for (const game of batch) {
      const preferred = venueOf(game.home);
      const alternate = otherVenue(game, preferred);
      if (openSlot(state, game, preferred, week) >= 0) assign(game, preferred);
      else if (budgeted && alternate !== preferred && designatedSaturated(game) && openSlot(state, game, alternate, week) >= 0) assign(game, alternate);
      else waiting.push(game);
    }

    if (budgeted) {
      const overloaded = () => [...load.keys()].find(id => load.get(id) > capacity.get(id));
      for (let from = overloaded(); from !== undefined; from = overloaded()) {
        const via = new Map([[from, null]]);
        const queue = [from];
        let reached = null;
        while (queue.length > 0 && reached === null) {
          const current = queue.shift();
          const candidates = [...hostedBy.get(current)].sort((a, b) => {
            const back = game => {
              const next = otherVenue(game, venueOfGame.get(game));
              return hostedCross.get(homeSquadAt(game, next).id) + 1 - crossTarget.get(homeSquadAt(game, next).id);
            };
            return back(a) - back(b);
          });
          for (const game of candidates) {
            const next = otherVenue(game, venueOfGame.get(game));
            if (next === venueOfGame.get(game) || via.has(next.id) || openSlot(state, game, next, week) < 0) continue;
            via.set(next.id, game);
            if (load.get(next.id) < capacity.get(next.id)) {
              reached = next;
              break;
            }
            queue.push(next.id);
          }
        }
        if (reached === null) {
          const excess = hostedBy.get(from);
          const game = excess[excess.length - 1];
          unassign(game);
          waiting.push(game);
          continue;
        }
        let to = reached;
        let game = via.get(to.id);
        while (game !== null) {
          const previous = venueOfGame.get(game);
          unassign(game);
          assign(game, to);
          to = previous;
          game = via.get(to.id);
        }
      }
    }

    const placed = [];
    for (const game of batch) {
      const loc = venueOfGame.get(game);
      if (!loc) continue;
      const slotIndex = openSlot(state, game, loc, week);
      if (slotIndex < 0) {
        waiting.push(game);
        continue;
      }
      const slot = slotsOf.get(loc.id)[slotIndex];
      const tableNumber = (state.slotLoad.get(`${loc.id}|${slotIndex}`) || 0) + 1;
      state.slotLoad.set(`${loc.id}|${slotIndex}`, tableNumber);
      for (const squad of [game.a, game.b]) {
        if (!state.bookings.has(squad.id)) {
          state.bookings.set(squad.id, { dayOfWeek: slot.dayOfWeek, locationId: loc.id, times: new Set() });
        }
        state.bookings.get(squad.id).times.add(slot.time);
      }
      const home = venueOf(game.home) === loc ? game.home : (game.home === game.a ? game.b : game.a);
      placed.push({ week, home, away: home === game.a ? game.b : game.a, game, loc, slot, tableNumber });
    }
    return { placed, waiting };
  }

  // Process passes round-by-round (full pass-by-pass): r0p0, r1p0, ..., r0p1, r1p1, ...
  // Within a pass-pair, mirror games are automatically separated by a full round-robin
  // cycle, preventing consecutive weeks with the same matchup.
  const pending = [];
  for (let pass = 0; pass < passes; pass++) {
    for (let roundIndex = 0; roundIndex < rounds.length; roundIndex++) {
      pending.push([...roundGames[pass][roundIndex]]);
    }
  }

  const placements = [];
  let firstUnfinished = 0;
  for (let week = 1; firstUnfinished < pending.length; week++) {
    for (let r = firstUnfinished; r < pending.length; r++) {
      if (pending[r].length === 0) continue;
      const { placed, waiting } = fitRound(pending[r], week);
      pending[r] = waiting;
      placements.push(...placed);
      if (placed.length === 0) break;
    }
    while (firstUnfinished < pending.length && pending[firstUnfinished].length === 0) firstUnfinished++;
  }

  const weekCount = placements.length > 0 ? placements[placements.length - 1].week : 0;
  const occurrencesByWeekday = new Map();
  for (let dayOfWeek = 0; dayOfWeek <= 6; dayOfWeek++) {
    occurrencesByWeekday.set(dayOfWeek, weekdayOccurrences({ sundayAlignedStart, dayOfWeek, count: weekCount }));
  }

  const events = [];
  for (const placement of placements) {
    const day = occurrencesByWeekday.get(placement.slot.dayOfWeek)[placement.week - 1];
    const date = new Date(day);
    date.setHours(Math.floor(placement.slot.time / 60), placement.slot.time % 60, 0, 0);
    events.push({
      week: placement.week,
      homeSquadId: placement.home.id,
      awaySquadId: placement.away.id,
      date,
      locationId: placement.loc.id,
      tableNumber: placement.tableNumber
    });
  }

  // ---------------------------------------------------------------------------
  // Blackout events for display — one entry per (week, dayOfWeek) that is in
  // the user-defined blackout set. Pre-start internal blackouts are excluded.
  // ---------------------------------------------------------------------------
  for (let week = 1; week <= weekCount; week++) {
    for (let dayOfWeek = 0; dayOfWeek <= 6; dayOfWeek++) {
      const date = new Date(sundayAlignedStart);
      date.setDate(date.getDate() + (week - 1) * 7 + dayOfWeek);
      const key = dateKey(date);
      if (blackoutSet.has(key)) {
        date.setHours(0, 0, 0, 0);
        events.push({
          week,
          isBlackout: true,
          date,
          homeSquadId: null,
          awaySquadId: null,
          locationId: null,
          tableNumber: null
        });
      }
    }
  }

  const bracketSize = league.bracketSize || 0;
  if (bracketSize >= 2) {
    const actualTeamCount = Math.min(bracketSize, squads.length);
    const bracketTree = buildBracketTree(actualTeamCount);
    const lastWeek = events.length > 0 ? events[events.length - 1].week : 0;
    const rounds = Math.log2(nextPowerOf2(actualTeamCount));
    for (const bt of bracketTree) {
      events.push({
        week: lastWeek + bt.round,
        round: bt.round,
        homeSquadId: null,
        awaySquadId: null,
        date: null,
        locationId: null,
        tableNumber: null,
        bracketSlot: bt.bracketSlot,
        homeSeed: bt.homeSeed,
        awaySeed: bt.awaySeed
      });
    }
    if (hasConsolation(actualTeamCount)) {
      events.push({
        week: lastWeek + rounds,
        round: rounds,
        homeSquadId: null,
        awaySquadId: null,
        date: null,
        locationId: null,
        tableNumber: null,
        bracketSlot: "THIRD",
        homeSeed: null,
        awaySeed: null
      });
    }
  }

  return { errors, events };
}
